import { Component, Input, Renderer2, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import * as L from 'leaflet';
import { DEMO_VESSEL_PC } from 'src/app/shared/helpers/heating-table-dynamic-extract.util';
import { PortRequestFormServiceService } from 'src/app/shared/service/port-request-FormService/port-request-form.service.service';
import { RequestFormService } from 'src/app/shared/service/requestForm/request-form.service';
declare const windyInit: any;
@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
})
export class MapComponent {
  userName: any;
  windy: any;
  data: any;
  ships: any[] = [];
  markers: L.Marker[] = [];
  private map!: L.Map;
  showGrayFlag: boolean = false;
  @Input() hoverActive: boolean = false;
  storedShipNames: any;
  isLoading: boolean = false;
  markersLayer: L.LayerGroup = L.layerGroup();
  subusername: any;
  constructor(
    private renderer: Renderer2,
    private requestFormService: RequestFormService,
    private prequestFormService: PortRequestFormServiceService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.userName = localStorage.getItem('username');
    this.subusername = localStorage.getItem('loginPic');
    this.storedShipNames = JSON.parse(
      localStorage.getItem('tableShipNames') || '[]',
    );
    this.initializeMap();
    if (this.userName !== 'Eiger Shipping') {
      this.loadData();
    }
  }

  /** True only for the 'grace' sub-user logged in under EMZoil specifically
   * — not EMZoil users in general, and not 'grace' under any other client. */
  private isEmzoilGrace(): boolean {
    return (
      this.userName === 'EMZoil Shipping' &&
      this.subusername?.toLowerCase() === 'grace'
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['hoverActive'] && this.hoverActive) {
      setTimeout(() => {
        if (this.map) {
          this.map.invalidateSize();
        }
      }, 200); // a slight delay to allow the div to fully appear
    }
  }

  loadData() {
    this.isLoading = true;
    this.prequestFormService.getMap().subscribe({
      next: (res: any) => {
        const rawList: any[] = Array.isArray(res) ? res : (res?.data || []);
        let filtered = rawList;

        // Step 1: Radio/Operator filter (only for Meraki - Operations)
        if (this.userName === 'Meraki - Operations') {
          const selectedRadio = localStorage.getItem('selectedRadio');
          if (selectedRadio === 'myShips') {
            const selectedOperator = localStorage.getItem('selectedOperator');
            if (selectedOperator) {
              filtered = filtered.filter(
                (item: any) => item.eigerpic === selectedOperator,
              );
            }
          }
        } else {
          // Step 2: Define client filters
          const clientFilters: { [key: string]: (item: any) => boolean } = {
            'Eiger Shipping': (item) =>
              (item.client_name || '').toLowerCase() === 'client',
            'Zeronorth ZN': (item) =>
              (item.client_name || '').toLowerCase() === 'zeronorth',
            'Elite Shipping': (item) => {
              const name = (item.client_name || '').toLowerCase();
              return name === 'elite' || name === 'elite tankship dmcc';
            },
            'Bunge Shipping': (item) =>
              (item.client_name || '').toLowerCase() === 'bunge',
            'Alcor Shipping': (item) =>
              (item.client_name || '').toLowerCase() === 'alcor chartering dmcc',
            'EMZoil Shipping': (item) => {
              const name = (item.client_name || item.client || '').toLowerCase();
              return name === 'emz oil' || name === 'emzoil fzco' || name === 'emzoil';
            },
            'Ethiopian Shipping & Logistics': (item) =>
              (item.client_name || item.client || '').toLowerCase() === 'ethiopian shipping & logistics',
          };

          if (this.userName && clientFilters[this.userName]) {
            filtered = filtered.filter(clientFilters[this.userName]);
          }
        }

        // Step 3b: EMZoil 'grace' sub-user sees a reduced fleet
        if (this.isEmzoilGrace()) {
          const allowedImos = ['9378876', '9399480', '9438406', '9432048'];
          filtered = filtered.filter((item: any) =>
            allowedImos.includes(String(item.imo))
          );
        }
        if (this.isDemoUser()) {
          filtered = filtered.filter((item: any) =>
            DEMO_VESSEL_PC.includes(String(item.imo))
          );
        }

        // Step 4: Assign filtered data to ships and render markers immediately
        this.data = filtered;
        this.ships = this.data;
        this.addMarkers();

        // Step 5: Enrich with live AIS data if available
        this.getAisData();
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to load map data', err);
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  getAisData() {
    if (!this.ships?.length) return;
    const imoNumbers = this.ships
      .map((s: any) => s.imo)
      .filter((imo: any) => !!imo);

    if (!imoNumbers.length) return;

    this.requestFormService.getkplerList(imoNumbers).subscribe({
      next: (res: any) => {
        const aisList = res?.data || [];

        // Map AIS by IMO (fast lookup)
        const aisMap = new Map();
        aisList.forEach((item: any) => {
          if (item?.AIS?.IMO) {
            aisMap.set(String(item.AIS.IMO), item.AIS);
          }
        });

        // Merge AIS into ships
        this.ships = this.ships.map((ship: any) => {
          return {
            ...ship,
            AIS: aisMap.get(String(ship.imo)) || ship.AIS || null,
          };
        });

        if (this.isEmzoilGrace()) {
          this.ships = this.ships?.filter(
            (ship: any) =>
              !(ship.vessel_name || ship.shipName || ship.AIS?.VESSEL_NAME || '')
                .toLowerCase()
                .includes('phosphor'),
          );
        }

        this.addMarkers(); // Re-render with updated live AIS positions and details
      },
      error: () => {
        // Keep existing markers rendered from report coordinates
      }
    });
  }
  getIconSize(zoomLevel: number): number {
    return Math.min(Math.max(zoomLevel * 2, 12), 40);
  }
  createIcon(
    voyageType: string,
    shipName: string,
    zoomLevel: number,
    heading: number = 0
  ): L.DivIcon {
    const size = this.getIconSize(zoomLevel) + 10;
    const isBallast = voyageType === 'Ballast';

    const hullColor = isBallast ? '#ff7a00' : '#7C2020';
    const cabinColor = '#ffffff';

    return L.divIcon({
      className: 'realistic-vessel-icon',
      html: `
      <div style="
        display:flex;
        flex-direction:column;
        align-items:center;
        transform: translateY(-6px);
      ">

        <!-- Rotating Ship -->
        <svg 
          width="${size * 1.6}" 
          height="${size * 0.8}" 
          viewBox="0 0 100 40"
          style="
            transform: rotate(${heading}deg);
            transform-origin: 50% 50%;
            filter: drop-shadow(0 3px 6px rgba(0,0,0,0.4));
          "
        >
          <polygon points="0,20 15,5 15,35" fill="${hullColor}" />
          <rect x="15" y="8" width="55" height="24" fill="${hullColor}" />
          <rect x="70" y="10" width="20" height="20"
            fill="${cabinColor}" stroke="${hullColor}" stroke-width="2"/>
          <rect x="90" y="12" width="6" height="16" fill="${hullColor}" />
        </svg>

        <div style="
          margin-top: -2px;
          font-size: 10px;
          font-weight: bold;
          color: midnightblue;
          white-space:nowrap;
        ">
          ${shipName || 'NA'}
        </div>
      </div>
    `,
      iconSize: [size * 1.8, size],
      iconAnchor: [size * 0.9, size / 2],
    });
  }
  removeShipPrefix(shipName: string): string {
    if (!shipName) {
      return '';
    }
    return shipName.replace(/^(MV|MT)\s*/, '');
  }
  convertToUTC(updatedAtString: any) {
    if (!updatedAtString) return '';
    const date = new Date(updatedAtString);
    if (isNaN(date.getTime())) return '';
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = monthNames[date.getUTCMonth()];
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');

    return `${day} ${month} ${hours}:${minutes}`;
  }

  addMarkers() {
    if (!this.map) return;
    this.markersLayer?.clearLayers();

    const validPoints: [number, number][] = [];

    this.ships.forEach((ship: any) => {
      const ais = ship.AIS;

      const rawLat = ais?.LATITUDE ?? ship.lat;
      const rawLng = ais?.LONGITUDE ?? ship.lng;

      if (
        rawLat === null ||
        rawLng === null ||
        rawLat === undefined ||
        rawLng === undefined ||
        rawLat === '' ||
        rawLng === ''
      ) {
        return;
      }

      const lat = Number(rawLat);
      const lng = Number(rawLng);

      if (isNaN(lat) || isNaN(lng)) return;

      validPoints.push([lat, lng]);

      // Heading fix (VERY IMPORTANT)
      const heading = (ais?.HEADING || ais?.COG || ship.heading || 0) + 90;
      const displayName = this.getDisplayVesselName(ship);
      const icon = this.createIcon(
        ship.condition,
        displayName,
        this.map.getZoom(),
        heading
      );

      const marker = L.marker([lat, lng], { icon });

      marker.addTo(this.markersLayer);

      const dest = ais?.DESTINATION || ship.destination || 'NA';
      const eta = ais?.ETA || (ship.eta ? this.convertToUTC(ship.eta) : 'NA');
      const navStatus = ais?.NAV_STATUS !== undefined
        ? this.getNavStatusLabel(ais.NAV_STATUS)
        : (ship.condition || 'Under way');
      const speed = ais?.SPEED ?? (ship.avg_speed_24h != null ? ship.avg_speed_24h : (ship.cp_speed ?? 'NA'));
      const cog = ais?.COG !== undefined && ais?.COG !== null ? this.formatCOG(ais.COG) : 'NA';
      const draught = ais?.DRAUGHT ?? (ship.draught ?? 'NA');
      const timestamp = ais?.TIMESTAMP || (ship.last_updated ? this.convertToUTC(ship.last_updated) : 'NA');
      const isLive = !!ais;

      const popup = `
    <div class="vessel-popup-card">
    
      <div class="popup-title">
        🚢 ${this.removeShipPrefix(displayName) || 'NA'}
        <span style="color:${isLive ? '#22c55e' : '#f59e0b'};font-size:11px;"> ● ${isLive ? 'LIVE' : 'REPORTED'}</span>
      </div>
    
      <div class="popup-grid">
    
      <div><i class="fa-solid fa-location-crosshairs "></i> Destination</div>
      <div>${dest}</div>
    
      <div><i class="fa-regular fa-clock "></i> ETA (UTC)</div>
      <div>${eta}</div>
    
      <div><i class="fa-solid fa-anchor "></i> Nav Status</div>
      <div>${navStatus}</div>
    
      <div><i class="fa-solid fa-gauge "></i> Speed (SOG)</div>
      <div>${speed} knots</div>
    
      <div><i class="fa-regular fa-compass "></i> Direction (COG)</div>
      <div>${cog}</div>
    
      <div><i class="fa-solid fa-arrow-down"></i> Depth</div>
      <div>${draught} m</div>
    
      <div><i class="fa-solid fa-location-dot"></i> Coordinates</div><div>${this.formatLatLng(lat, 'lat')}, ${this.formatLatLng(lng, 'lng')}</div>
      <div><i class="fa-regular fa-clock"></i> UTC Time</div><div>${timestamp} ${ais?.TIMESTAMP ? this.getTimeAgo(ais.TIMESTAMP) : ''}</div>
    </div>
    
    </div>
    `;

      // Bind popup
      marker.bindPopup(popup, {
        closeButton: false,
        offset: [0, -5],
        autoPan: false,
        className: 'vessel-popup',
      });

      marker.on('mouseover', () => marker.openPopup());
      marker.on('mouseout', () => marker.closePopup());
    });

    // Auto-fit bounds if client user has vessels
    if (this.userName && this.userName !== 'Meraki - Operations' && validPoints.length > 0) {
      if (validPoints.length === 1) {
        this.map.setView(validPoints[0], 5);
      } else {
        const bounds = L.latLngBounds(validPoints);
        this.map.fitBounds(bounds, { padding: [40, 40], maxZoom: 6 });
      }
    }
  }

  initializeMap(): void {
    this.map = L.map('windy', {
      center: [34.553127, 18.048012],
      zoom: 1.5,
      minZoom: 1,
      zoomControl: true,
    });

    // Add CARTO basemap
    L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
      {
        subdomains: ['a', 'b', 'c', 'd'],
      },
    ).addTo(this.map);

    // Add markers layer to map
    this.markersLayer.addTo(this.map);

    // Fix rendering when container is hidden
    setTimeout(() => {
      this.map.invalidateSize();
    }, 200);

    // If API data already came before map loaded → add markers
    if (this.ships && this.ships.length > 0) {
      this.initMarkers();
    }
    this.map.createPane('tilePane').style.zIndex = '0';
  }

  initMarkers(): void {
    this.addMarkers();
  }
  addWeatherLayer(windyAPI: any) {
    setTimeout(() => {
      windyAPI.store.set('timestamp', Date.now()); // This forces the Windy layer to update/redraw
    }, 500); // Adjust the timeout value if necessary
  }

  getTimeAgo(timestamp: string): string {
    if (!timestamp) return '';

    const updatedTime = new Date(timestamp).getTime();
    const now = new Date().getTime();

    const diffMs = now - updatedTime;

    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));

    if (minutes < 1) return '(just now)';
    if (minutes < 60) return `(${minutes} min ago)`;
    if (hours < 24) return `(${hours} hr ago)`;

    const days = Math.floor(hours / 24);
    return `(${days} day${days > 1 ? 's' : ''} ago)`;
  }
  getDirection(deg: number): string {
    if (deg == null) return '';

    const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    return dirs[Math.round(deg / 45) % 8];
  }

  formatCOG(cog: number): string {
    if (cog == null) return 'NA';
    return `${cog}° (${this.getDirection(cog)})`;
  }
  formatROT(rot: number): string {
    if (rot == null) return 'NA';
    if (rot === 0) return 'Straight';

    return rot > 0 ? `Right (${rot})` : `Left (${rot})`;
  }
  formatLatLng(value: number, type: 'lat' | 'lng'): string {
    if (value === null || value === undefined) return 'NA';

    const abs = Math.abs(value).toFixed(2);

    if (type === 'lat') {
      return `${abs}° ${value >= 0 ? 'N' : 'S'}`;
    } else {
      return `${abs}° ${value >= 0 ? 'E' : 'W'}`;
    }
  }

  getNavStatusLabel(status: number): string {
    const map: any = {
      0: 'Under way',
      1: 'At anchor',
      2: 'Not under command',
      3: 'Restricted maneuverability',
      4: 'Constrained by draught',
      5: 'Moored',
      6: 'Aground',
      7: 'Fishing',
      8: 'Moving under sail',
    };
    return map[status] || 'Unknown';
  }

  formatCourse(cog: number): string {
    if (cog == null) return 'NA';
    return `${cog}°`;
  }

  formatHeading(heading: number): string {
    if (heading == null) return 'NA';
    return `${heading}° (${this.getDirection(heading)})`;
  }

  private isDemoUser(): boolean {
    return this.subusername?.toLowerCase() === 'demo';
  }

  private isDemoVessel(imo: any): boolean {
    return DEMO_VESSEL_PC.includes(String(imo));
  }

  private getMaskedVesselName(imo: any): string {
    const index = DEMO_VESSEL_PC.indexOf(String(imo));

    return index >= 0 ? `Vessel ${index + 1}` : 'Vessel';
  }

  private getDisplayVesselName(ship: any): string {
    if (this.isDemoUser() && this.isDemoVessel(ship.imo)) {
      return this.getMaskedVesselName(ship.imo);
    }

    return ship.vessel_name || ship.shipName || ship.AIS?.VESSEL_NAME || 'NA';
  }

}
